import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BellIcon,
  DocumentTextIcon,
  UserGroupIcon,
  HeartIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  TrophyIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";

const Dashboard = () => {
  const [notifications] = useState(3);

  const kpiData = [
    {
      title: "Patient Satisfaction",
      value: "94.2%",
      change: "+2.1%",
      trend: "up",
      status: "excellent",
      icon: HeartIcon,
      color: "from-red-500 to-pink-600",
    },
    {
      title: "System Uptime",
      value: "99.7%",
      change: "+0.3%",
      trend: "up",
      status: "excellent",
      icon: ShieldCheckIcon,
      color: "from-green-500 to-emerald-600",
    },
    {
      title: "Staff Efficiency",
      value: "87.3%",
      change: "+4.2%",
      trend: "up",
      status: "good",
      icon: UserGroupIcon,
      color: "from-teal-500 to-emerald-600",
    },
    {
      title: "Bed Occupancy",
      value: "85.1%",
      change: "+1.5%",
      trend: "up",
      status: "good",
      icon: BuildingOffice2Icon,
      color: "from-purple-500 to-violet-600",
    },
    {
      title: "Monthly Revenue",
      value: "$12.4M",
      change: "+5.7%",
      trend: "up",
      status: "excellent",
      icon: CurrencyDollarIcon,
      color: "from-yellow-500 to-orange-600",
    },
    {
      title: "Quality Score",
      value: "96.8%",
      change: "+3.1%",
      trend: "up",
      status: "excellent",
      icon: TrophyIcon,
      color: "from-apollo-accent to-green-600",
    },
  ];

  const clinicalBriefs = [
    {
      id: 1,
      title: "Emergency Response Protocol Update",
      type: "SOP",
      priority: "high",
      status: "active",
      lastUpdated: "2 hours ago",
      department: "Emergency Medicine",
    },
    {
      id: 2,
      title: "Medication Administration Guidelines",
      type: "SOP",
      priority: "medium",
      status: "under_review",
      lastUpdated: "1 day ago",
      department: "Pharmacy",
    },
    {
      id: 3,
      title: "Patient Safety Incident Report",
      type: "Incident",
      priority: "low",
      status: "resolved",
      lastUpdated: "3 days ago",
      department: "Quality Assurance",
    },
  ];

  const boardDocs = [
    {
      title: "Q4 2024 Financial Report",
      date: "Jan 15, 2025",
      type: "Financial",
      status: "new",
    },
    {
      title: "Strategic Plan 2025-2027",
      date: "Jan 10, 2025",
      type: "Strategic",
      status: "updated",
    },
    {
      title: "Board Meeting Minutes - December",
      date: "Jan 05, 2025",
      type: "Governance",
      status: "reviewed",
    },
    {
      title: "Risk Assessment Annual Review",
      date: "Dec 28, 2024",
      type: "Risk Management",
      status: "pending",
    },
  ];

  const recentAlerts = [
    {
      id: 1,
      message: "System maintenance scheduled for this weekend",
      type: "info",
      time: "10 minutes ago",
    },
    {
      id: 2,
      message: "New compliance requirement effective February 1st",
      type: "warning",
      time: "1 hour ago",
    },
    {
      id: 3,
      message: "Monthly board report ready for review",
      type: "success",
      time: "Updated",
      icon: CheckCircleIcon,
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "excellent":
        return "text-green-600 bg-green-50 border-green-200";
      case "good":
        return "text-teal-600 bg-teal-50 border-teal-200";
      case "warning":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "danger":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high":
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case "medium":
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case "low":
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-green-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-emerald-100 rounded-full opacity-20 blur-2xl"></div>
      </div>

      {/* Enhanced Header */}
      <header className="relative bg-white shadow-xl border-b-2 border-apollo-accent/20 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
            {/* Left Side - Logo and Title */}
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <img
                  src="https://www.apollohospitals.com/themes/custom/apollo/assets/svg/apollo-logo.svg"
                  alt="Apollo Hospitals"
                  className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
                />
                <div className="border-l-2 border-apollo-accent/30 pl-4">
                  <div className="text-xl font-bold text-apollo-primary">
                    Executive Console
                  </div>
                  <div className="text-xs text-apollo-accent font-medium">
                    Apollo Hospitals Noida
                  </div>
                </div>
              </Link>
            </div>

            {/* Right Side - Stats, Notifications, Profile */}
            <div className="flex items-center space-x-6">
              {/* Quick Stats */}
              <div className="hidden lg:flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-apollo-secondary font-medium">
                    All Systems Operational
                  </span>
                </div>
                <div className="text-apollo-secondary">
                  <span className="font-semibold">1,247</span> Active Patients
                </div>
              </div>

              {/* Notifications */}
              <div className="relative">
                <button className="p-3 text-gray-600 hover:text-apollo-primary hover:bg-apollo-gray rounded-xl transition-all duration-300 relative">
                  <BellIcon className="w-6 h-6" />
                  {notifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold">
                      {notifications}
                    </span>
                  )}
                </button>
              </div>

              {/* User Profile */}
              <div className="flex items-center space-x-3 bg-apollo-gray rounded-2xl px-4 py-2">
                <div className="w-10 h-10 bg-gradient-to-r from-apollo-accent to-green-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-sm font-bold">SM</span>
                </div>
                <div>
                  <div className="text-sm font-bold text-apollo-primary">
                    Dr. Sarah Mitchell
                  </div>
                  <div className="text-xs text-apollo-secondary">
                    Chief Medical Officer
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Enhanced KPI Panel */}
        <div className="mb-8 sm:mb-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-8 gap-4 sm:gap-0">
            <h2 className="text-2xl sm:text-3xl font-bold text-apollo-primary">
              Key Performance Indicators
            </h2>
            <button className="bg-apollo-accent text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-sm sm:text-base">
              View Full Report
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {kpiData.map((kpi, index) => {
              const IconComponent = kpi.icon;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-apollo-accent/10 hover:border-apollo-accent/30"
                >
                  <div className="flex items-start justify-between mb-4 sm:mb-6">
                    <div
                      className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${kpi.color} rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:shadow-lg transition-shadow`}
                    >
                      <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <div
                      className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold border ${getStatusColor(
                        kpi.status
                      )}`}
                    >
                      {kpi.status}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-3xl font-bold text-apollo-primary">
                      {kpi.value}
                    </div>
                    <div className="text-lg font-medium text-apollo-secondary">
                      {kpi.title}
                    </div>
                    <div className="flex items-center space-x-2">
                      <ArrowTrendingUpIcon
                        className={`w-4 h-4 ${
                          kpi.trend === "up" ? "text-green-500" : "text-red-500"
                        }`}
                      />
                      <span
                        className={`text-sm font-semibold ${
                          kpi.trend === "up" ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {kpi.change} from last month
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Enhanced Dashboard Widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
          {/* Clinical Operations Widget */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-apollo-accent/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-apollo-primary">
                Clinical Operations
              </h3>
              <BeakerIcon className="w-8 h-8 text-apollo-accent" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-apollo-gray rounded-2xl">
                <span className="font-medium text-apollo-secondary">
                  Surgery Queue
                </span>
                <span className="text-2xl font-bold text-apollo-primary">
                  24
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-2xl">
                <span className="font-medium text-green-700">
                  ICU Availability
                </span>
                <span className="text-2xl font-bold text-green-600">
                  12 beds
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-orange-50 rounded-2xl">
                <span className="font-medium text-orange-700">
                  Emergency Cases
                </span>
                <span className="text-2xl font-bold text-orange-600">
                  8 active
                </span>
              </div>
            </div>
          </div>

          {/* Financial Overview Widget */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg border border-apollo-accent/10">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-apollo-primary">
                Financial Overview
              </h3>
              <CurrencyDollarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
            </div>
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-3xl sm:text-4xl font-bold text-green-600 mb-2">
                $12.4M
              </div>
              <div className="text-apollo-secondary mb-4">Monthly Revenue</div>
              <div className="flex justify-center items-center space-x-2">
                <ArrowTrendingUpIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                <span className="text-green-600 font-semibold text-sm sm:text-base">
                  +5.7% from last month
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="text-center p-3 bg-apollo-gray rounded-xl">
                <div className="font-bold text-apollo-primary">$2.1M</div>
                <div className="text-sm text-apollo-secondary">Weekly</div>
              </div>
              <div className="text-center p-3 bg-apollo-gray rounded-xl">
                <div className="font-bold text-apollo-primary">$142M</div>
                <div className="text-sm text-apollo-secondary">Annual</div>
              </div>
            </div>
          </div>

          {/* Staff Performance Widget */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-apollo-accent/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-apollo-primary">
                Staff Performance
              </h3>
              <TrophyIcon className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-apollo-secondary">
                  Attendance Rate
                </span>
                <span className="text-xl font-bold text-green-600">96.8%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full"
                  style={{ width: "96.8%" }}
                ></div>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-apollo-secondary">
                  Training Completion
                </span>
                <span className="text-xl font-bold text-teal-600">89.2%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-teal-500 to-emerald-600 h-3 rounded-full"
                  style={{ width: "89.2%" }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Clinical Briefs & Board Docs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
          {/* Clinical Briefs */}
          <div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-apollo-accent/10">
              <h2 className="text-xl font-semibold text-apollo-primary mb-6">
                Clinical Briefs
              </h2>
              <div className="space-y-4">
                {clinicalBriefs.map((brief) => (
                  <div
                    key={brief.id}
                    className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {getPriorityIcon(brief.priority)}
                        <h3 className="font-medium text-apollo-secondary">
                          {brief.title}
                        </h3>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          brief.type === "SOP"
                            ? "bg-teal-100 text-teal-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {brief.type}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span
                        className={`px-2 py-1 rounded-full ${
                          brief.status === "active"
                            ? "bg-green-100 text-green-700"
                            : brief.status === "under_review"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {brief.status.replace("_", " ")}
                      </span>
                      <span className="text-gray-500">{brief.lastUpdated}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Board Documents */}
          <div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-apollo-accent/10">
              <h2 className="text-xl font-semibold text-apollo-primary mb-6">
                Board Documents & Reports
              </h2>
              <div className="space-y-4">
                {boardDocs.map((doc, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-apollo-secondary">
                        {doc.title}
                      </h3>
                      <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-apollo-accent">{doc.type}</span>
                      <span className="text-gray-500">{doc.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Notifications */}
        <div>
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-apollo-accent/10">
            <h2 className="text-xl font-semibold text-apollo-primary mb-6">
              Recent Notifications
            </h2>
            <div className="space-y-3">
              {recentAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-4 rounded-lg border-l-4 ${
                    alert.type === "success"
                      ? "border-l-green-500 bg-green-50"
                      : alert.type === "warning"
                      ? "border-l-yellow-500 bg-yellow-50"
                      : "border-l-apollo-accent bg-apollo-gray"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <p className="text-sm text-apollo-secondary">
                      {alert.message}
                    </p>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                      {alert.time}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
